// MediaList.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { message, Table } from 'antd';

import { Display } from '../../../CRM/organism/Main/styles';
import { HomeTownRoundCheckbox } from '../../../app/organisms/Auth/Forms/HomeTownRoundCheckbox';
import { TwoStepListItem } from '../CustomerCommercial/atom/TwoStepListItem';
import { RealEstateInfo } from '../CustomerCommercial/blocks/RealEstateInfo';
import { useAdvertiserAgentList } from '../../hooks/Advertiser/CustomerCommercial/GetAdvertiserAgentList';
import { MediaItem } from './blocks/MediaItem';
import { SelectAddressItem } from './blocks/SelectAddressItem';

declare global {
  interface Window {
    naver: any;
  }
}

/* ===================== 튜닝 상수 ===================== */
const DEFAULT_ZOOM = 14; // 초기/현재위치 이동 시 기본 줌
const GEOCODE_ZOOM = 14; // 주소 선택/첫번째 항목 센터 이동 시 줌
const FIT_BOUNDS_PADDING = { top: 60, right: 60, bottom: 60, left: 60 };
const FIT_BOUNDS_ZOOM_MAX = 12; // fitBounds 후 너무 가까우면 상한으로 캡
const FIT_BOUNDS_ZOOM_OFFSET = -1; // fitBounds 후 한 단계 멀어지기(0이면 비활성)
const USER_INTERACT_SUPPRESS_MS = 1500; // 드래그/휠 후 자동 fitBounds 억제 시간(ms)

/* =============== 공용 유틸 =============== */
type LatLng = { lat: number; lng: number };

const toLatLng = (y?: any, x?: any): LatLng | null => {
  const lat = parseFloat(y);
  const lng = parseFloat(x);
  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

const makeFullAddress = (a?: string | null, b?: string | null, c?: string | null) =>
    `${a || ''} ${b || ''} ${c || ''}`.trim();

/* =============== 지오코딩 훅 (캐시 + 디바운스) =============== */
function useNaverGeocoder() {
  const cacheRef = useRef<Map<string, LatLng>>(new Map());
  const pendingRef = useRef<Map<string, Promise<LatLng | null>>>(new Map());
  const debounceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const geocode = (query: string, delay = 0): Promise<LatLng | null> => {
    if (!query) return Promise.resolve(null);
    const cached = cacheRef.current.get(query);
    if (cached) return Promise.resolve(cached);

    const pending = pendingRef.current.get(query);
    if (pending) return pending;

    const runner = new Promise<LatLng | null>((resolve) => {
      const exec = () => {
        const { naver } = window;
        if (!naver?.maps?.Service || !naver?.maps?.Service?.geocode) {
          resolve(null);
          return;
        }
        naver.maps.Service.geocode({ query }, (status: any, response: any) => {
          if (status !== naver.maps.Service.Status.OK) {
            resolve(null);
            return;
          }
          const a = response?.v2?.addresses?.[0];
          const ll = toLatLng(a?.y, a?.x);
          if (ll) cacheRef.current.set(query, ll);
          resolve(ll);
        });
      };

      if (delay > 0) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = window.setTimeout(exec, delay);
      } else {
        exec();
      }
    }).finally(() => {
      pendingRef.current.delete(query);
    });

    pendingRef.current.set(query, runner);
    return runner;
  };

  return { geocode, cacheRef };
}

/* ========================================================= */

export const MediaList: React.FC = () => {
  // 위치/상태
  const [userLocation, setUserLocation] = useState<LatLng>({ lat: 37.5665, lng: 126.978 });
  const [realEstateId, setRealEstateId] = useState<number | null>(null);
  const [mapReady, setMapReady] = useState<boolean>(() => !!window?.naver?.maps);
  const [mapLoadFailed, setMapLoadFailed] = useState(false);

  // 주소 선택 (디자인/props 유지)
  const [selectedFirstName, setSelectedFirstName] = React.useState<any>(null);
  const [selectedSecondName, setSelectedSecondName] = React.useState<any>(null);
  const [selectedThirdName, setSelectedThirdName] = React.useState<any>(null);

  // 파생값: 풀주소
  const fullAddress = useMemo(
      () => makeFullAddress(selectedFirstName, selectedSecondName, selectedThirdName),
      [selectedFirstName, selectedSecondName, selectedThirdName]
  );

  // 데이터
  const { advertiserAgentList } = useAdvertiserAgentList(1, 999999999, fullAddress || '');

  // 지도/마커
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const fitBoundsTimerRef = useRef<number | null>(null);

  // 사용자 상호작용 억제 플래그
  const userInteractingRef = useRef(false);
  const suppressAutofitUntilRef = useRef(0);

  // 지오코더
  const { geocode } = useNaverGeocoder();

  // 네이버맵 스크립트 로딩 감시 (미로딩/실패 시 지도 로직 스킵)
  useEffect(() => {
    let cancelled = false;
    let rafId: number | null = null;
    let timeoutId: number | null = null;

    const stop = () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const checkReady = () => {
      if (cancelled) return;
      if (window.naver?.maps) {
        setMapReady(true);
        setMapLoadFailed(false);
        return;
      }
      rafId = requestAnimationFrame(checkReady);
    };

    if (!mapReady) {
      checkReady();
      timeoutId = window.setTimeout(() => {
        if (!window.naver?.maps && !cancelled) {
          setMapLoadFailed(true);
          stop();
        }
      }, 4000);
    }

    return () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [mapReady]);

  /* =============== 안전한 fitBounds 유틸 =============== */
const safeFitBounds = (bounds: any) => {
    const { naver } = window;
    if (!mapRef.current || !naver?.maps || !naver.maps.Event) return;

    // 1) 사용자 제스처 중/직후엔 자동 맞춤 금지
    if (userInteractingRef.current || Date.now() < suppressAutofitUntilRef.current) return;

    // 2) 빈 bounds 방어
    if (!bounds || typeof bounds.isEmpty !== 'function' || bounds.isEmpty()) return;

    mapRef.current.fitBounds(bounds, FIT_BOUNDS_PADDING);

    // 3) idle 1회만 줌 보정
    const once = naver.maps.Event.addListener(mapRef.current, 'idle', () => {
      if (!mapRef.current) return;
      const current = mapRef.current.getZoom();
      const capped = Math.min(current, FIT_BOUNDS_ZOOM_MAX);
      const target = capped + FIT_BOUNDS_ZOOM_OFFSET;
      mapRef.current.setZoom(target);
      naver.maps.Event.removeListener(once);
    });
  };

  /* =============== 지도 초기화 =============== */
  useEffect(() => {
    let mounted = true;

    if (!mapReady || mapLoadFailed) return;
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const init = () => {
      try {
        if (!mounted || !window.naver?.maps) return;
        const { naver } = window;

        mapRef.current = new naver.maps.Map(mapContainerRef.current, {
          center: new naver.maps.LatLng(userLocation.lat, userLocation.lng),
          zoom: DEFAULT_ZOOM,
        });

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
              (pos) => {
                if (!mounted || !mapRef.current) return;
                const { latitude, longitude } = pos.coords;
                const ll = { lat: latitude, lng: longitude };
                setUserLocation(ll);
                mapRef.current.setCenter(new naver.maps.LatLng(ll.lat, ll.lng));
                mapRef.current.setZoom(DEFAULT_ZOOM);
              },
              () => {}
          );
        }
      } catch (err) {
        console.error('네이버 지도 초기화 실패', err);
        setMapLoadFailed(true);
      }
    };

    if (window.naver?.maps) {
      init();
    } else {
      const onReady = () => init();
      window.addEventListener('naver-maps-ready', onReady);

      const tick = () => {
        if (!mounted) return;
        if (window.naver?.maps) init();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

      return () => {
        window.removeEventListener('naver-maps-ready', onReady);
      };
    }

    return () => {
      mounted = false;

      if (fitBoundsTimerRef.current) {
        clearTimeout(fitBoundsTimerRef.current);
        fitBoundsTimerRef.current = null;
      }

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };
  }, [mapLoadFailed, mapReady]);

  /* =============== 사용자 제스처 감지(드래그/휠/터치) =============== */
  useEffect(() => {
    if (!mapReady || mapLoadFailed) return;
    if (!mapRef.current || !window.naver?.maps?.Event) return;
    const { naver } = window;

    const markInteract = () => {
      userInteractingRef.current = true;
      suppressAutofitUntilRef.current = Date.now() + USER_INTERACT_SUPPRESS_MS;
      // 일정 시간 뒤 자동 해제 (이 사이에 추가 제스처가 있으면 시간이 갱신됨)
      setTimeout(() => {
        if (Date.now() >= suppressAutofitUntilRef.current) {
          userInteractingRef.current = false;
        }
      }, USER_INTERACT_SUPPRESS_MS + 50);
    };

    const dragStart = naver.maps.Event.addListener(mapRef.current, 'dragstart', markInteract);
    const zoomChanged = naver.maps.Event.addListener(mapRef.current, 'zoom_changed', markInteract);
    const touchStart = naver.maps.Event.addListener(mapRef.current, 'touchstart', markInteract);

    return () => {
      naver.maps.Event.removeListener(dragStart);
      naver.maps.Event.removeListener(zoomChanged);
      naver.maps.Event.removeListener(touchStart);
    };
  }, [mapLoadFailed, mapReady]);

  /* =============== 주소 선택 시 지도 이동(선택 UI) =============== */
  useEffect(() => {
    if (!mapReady || mapLoadFailed) return;
    if (!mapRef.current || !fullAddress || !window.naver?.maps) return;
    geocode(fullAddress, 120) // 살짝 디바운스
        .then((ll) => {
          if (ll && mapRef.current) {
            mapRef.current.setCenter(new window.naver.maps.LatLng(ll.lat, ll.lng));
            mapRef.current.setZoom(GEOCODE_ZOOM);
          }
        })
        .catch(() => {
          /* noop */
        });
  }, [fullAddress, geocode, mapLoadFailed, mapReady]);

  /* =============== 리스트 변경 시 마커 재생성 + 첫번째 주소 센터 고정 =============== */
  useEffect(() => {
    if (!mapReady || mapLoadFailed) return;
    if (!mapRef.current || !window.naver?.maps) return;
    const { naver } = window;

    // 기존 마커 제거
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (fitBoundsTimerRef.current) {
      clearTimeout(fitBoundsTimerRef.current);
      fitBoundsTimerRef.current = null;
    }

    if (!advertiserAgentList?.length) return;

    const bounds = new naver.maps.LatLngBounds();

    // 공통: 좌표 → 마커 추가
    const addMarker = (lat: number, lng: number) => {
      const position = new naver.maps.LatLng(lat, lng);
      const marker = new naver.maps.Marker({ position, map: mapRef.current });
      markersRef.current.push(marker);
      bounds.extend(position);
    };

    (async () => {
      // 1) 첫 번째 주소로 센터 이동 & 첫 마커
      const first = advertiserAgentList[0];
      if (first?.address) {
        const ll = await geocode(first.address, 0);
        if (ll && mapRef.current) {
          addMarker(ll.lat, ll.lng);
          mapRef.current.setCenter(new naver.maps.LatLng(ll.lat, ll.lng));
          mapRef.current.setZoom(GEOCODE_ZOOM);
        }
      }

      // 2) 나머지 항목 마커 추가
      for (let i = 1; i < advertiserAgentList.length; i += 1) {
        const item = advertiserAgentList[i];
        if (!item?.address) continue;
        // eslint-disable-next-line no-await-in-loop
        const ll = await geocode(item.address, 60);
        if (ll && mapRef.current) addMarker(ll.lat, ll.lng);
      }

      // 3) 전체 영역 보이기 원하면 주석 해제 (현재는 요구대로 첫번째 기준으로 고정)
      // safeFitBounds(bounds);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserAgentList, geocode, mapLoadFailed, mapReady]);

  /* =============== 테이블 컬럼 (디자인 유지) =============== */
  const columns = useMemo(
      () => [
        {
          dataIndex: '',
          title: '',
          render: (_: any, data: any) => (
              <MediaItem
                  setRealEstateId={setRealEstateId}
                  agentId={data?.agentId}
                  address={data?.address}
                  businessHours={data?.businessHours}
                  imageUrl={data?.imageUrl}
                  mediaInfo={data?.mediaInfo}
                  store={data?.store}
                  mediaType={data?.mediaType}
              />
          ),
        },
      ],
      []
  );

  return (
      <>
        <Display>
          <SelectAddressItem
              selectedFirstName={selectedFirstName}
              setSelectedFirstName={setSelectedFirstName}
              selectedSecondName={selectedSecondName}
              setSelectedSecondName={setSelectedSecondName}
              selectedThirdName={selectedThirdName}
              setSelectedThirdName={setSelectedThirdName}
          />

          <ContentsSection>
            {/* 지도 영역 (디자인 변경 없음) */}
            <MapArea>
              <MapCanvas ref={mapContainerRef} />
              {!mapReady && !mapLoadFailed && (
                  <MapStatus>지도를 불러오는 중입니다…</MapStatus>
              )}
              {mapLoadFailed && (
                  <MapStatus>네이버 지도 스크립트를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.</MapStatus>
              )}
            </MapArea>

            <RightContents>
              <ListHeader>
                {`${advertiserAgentList?.length || 0}곳의 공인중개사무소`}
                {/* <RoundCheckWrapper>
                <HomeTownRoundCheckbox checked text="인기순" />
                <HomeTownRoundCheckbox checked={false} text="최신순" />
              </RoundCheckWrapper> */}
              </ListHeader>

              <ListContent>
                <Table
                    columns={columns as any}
                    dataSource={advertiserAgentList}
                    pagination={{ pageSize: 10, position: ['bottomLeft'] }}
                    showHeader={false}
                    rowKey={(r: any) => r?.agentId ?? r?.id ?? r?.address}
                />
              </ListContent>
            </RightContents>
          </ContentsSection>
        </Display>

        {realEstateId !== null && (
            <RealEstateInfo setRealEstateId={setRealEstateId} realEstateId={realEstateId} />
        )}
      </>
  );
};

/* ------- styles (디자인 그대로) ------- */
const MapArea = styled.div`
  position: relative;
  width: 68%;
  height: 785px;
`;

const MapCanvas = styled.div`
  width: 100%;
  height: 100%;
`;

const MapStatus = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  color: #111;
  font-weight: 600;
  border: 1px solid #0000000d;
  border-radius: 4px;
`;

const RightContents = styled.div``;

const ListHeader = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 19.09px;
  margin-bottom: 10px;
  padding: 0px 12px;
  display: flex;
  justify-content: space-between;
`;

const RoundCheckWrapper = styled.div`
  display: flex;
  gap: 14px;
`;

const ListContent = styled.div`
  border-bottom: 1px solid #0000000d;
  border-top: 1px solid #0000000d;
  height: 785px;
  overflow: scroll;
`;

const ContentsSection = styled.div`
  display: flex;
  gap: 12px;
`;
